import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import logger from '../services/logger';
import './AdminPanel.css';

interface LogEntry {
  id: number;
  timestamp: string;
  level: string;
  category: string;
  message: string;
  context: any;
  user_id?: string;
  session_id?: string;
  request_id?: string;
  endpoint?: string;
  method?: string;
  status_code?: number;
  duration_ms?: number;
}

interface UserActivity {
  id: number;
  auth0_user_id: string;
  activity_type: string;
  timestamp: string;
  facility_id?: string;
  location_lat?: number;
  location_lon?: number;
  user_role: string;
}

interface SessionSummary {
  session_id: string;
  user_id: string;
  start_time: string;
  last_activity: string;
  activity_count: number;
  pages_visited: string[];
}

interface User {
  id: number;
  auth0_user_id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
  last_login: string;
  is_active: boolean;
}

const AdminPanel: React.FC = () => {
  const { user } = useAuth0();
  const [activeTab, setActiveTab] = useState<'logs' | 'activities' | 'users' | 'sessions' | 'analytics'>('logs');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [userActivities, setUserActivities] = useState<UserActivity[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [logFilter, setLogFilter] = useState<string>('');
  const [logLevel, setLogLevel] = useState<string>('ALL');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load user activities first, then sessions (which depend on activities)
      await loadUserActivities();
      await loadLogs();
      await loadUsers();
      await loadSessions();
    } catch (error) {
      logger.error('ADMIN_PANEL', 'Failed to load admin data', { error });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    logger.logUserAction('admin_panel_accessed', { 
      adminUserId: user?.sub,
      tab: activeTab 
    });
    loadData();
  }, [activeTab, user?.sub, loadData]);

  const loadLogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/logs?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        logger.warn('ADMIN_PANEL', 'Failed to load logs', { status: response.status });
        setLogs([]);
      }
    } catch (error) {
      logger.error('ADMIN_PANEL', 'Error loading logs', { error });
      setLogs([]);
    }
  };

  const loadUserActivities = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/user-activities?limit=50`);
      if (response.ok) {
        const data = await response.json();
        setUserActivities(data.activities || []);
      } else {
        logger.warn('ADMIN_PANEL', 'Failed to load user activities', { status: response.status });
        setUserActivities([]);
      }
    } catch (error) {
      logger.error('ADMIN_PANEL', 'Error loading user activities', { error });
      setUserActivities([]);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        logger.warn('ADMIN_PANEL', 'Failed to load users', { status: response.status });
        setUsers([]);
      }
    } catch (error) {
      logger.error('ADMIN_PANEL', 'Error loading users', { error });
      setUsers([]);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/sessions?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        logger.warn('ADMIN_PANEL', 'Failed to load sessions', { status: response.status });
        // Generate sessions from user activities as fallback
        generateSessionsFromActivities();
      }
    } catch (error) {
      logger.error('ADMIN_PANEL', 'Error loading sessions', { error });
      generateSessionsFromActivities();
    }
  };

  const generateSessionsFromActivities = () => {
    // Generate session summaries from user activities data
    const sessionMap = new Map<string, SessionSummary>();
    
    userActivities.forEach(activity => {
      const sessionKey = `${activity.auth0_user_id}_${new Date(activity.timestamp).toDateString()}`;
      
      if (!sessionMap.has(sessionKey)) {
        sessionMap.set(sessionKey, {
          session_id: sessionKey,
          user_id: activity.auth0_user_id,
          start_time: activity.timestamp,
          last_activity: activity.timestamp,
          activity_count: 1,
          pages_visited: [activity.activity_type]
        });
      } else {
        const session = sessionMap.get(sessionKey)!;
        session.activity_count++;
        session.last_activity = activity.timestamp;
        if (!session.pages_visited.includes(activity.activity_type)) {
          session.pages_visited.push(activity.activity_type);
        }
      }
    });

    setSessions(Array.from(sessionMap.values()).slice(0, 20));
  };

  const filteredLogs = logs.filter(log => {
    const matchesFilter = !logFilter || 
      log.message.toLowerCase().includes(logFilter.toLowerCase()) ||
      log.category.toLowerCase().includes(logFilter.toLowerCase());
    const matchesLevel = logLevel === 'ALL' || log.level === logLevel;
    return matchesFilter && matchesLevel;
  });

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getLogLevelClass = (level: string) => {
    switch (level) {
      case 'ERROR': return 'log-error';
      case 'WARN': return 'log-warn';
      case 'INFO': return 'log-info';
      default: return 'log-debug';
    }
  };

  const exportLogs = () => {
    const dataStr = JSON.stringify(filteredLogs, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `logs-export-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    logger.logUserAction('admin_logs_exported', { 
      exportCount: filteredLogs.length,
      adminUserId: user?.sub 
    });
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <p>System monitoring and user management</p>
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          System Logs
        </button>
        <button 
          className={`tab-button ${activeTab === 'activities' ? 'active' : ''}`}
          onClick={() => setActiveTab('activities')}
        >
          User Activities
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          User Management
        </button>
        <button 
          className={`tab-button ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Active Sessions
        </button>
        <button 
          className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      {loading && <div className="loading">Loading admin data...</div>}

      {activeTab === 'logs' && (
        <div className="tab-content">
          <div className="logs-controls">
            <div className="filter-group">
              <input
                type="text"
                placeholder="Filter logs..."
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
                className="filter-input"
              />
              <select 
                value={logLevel} 
                onChange={(e) => setLogLevel(e.target.value)}
                className="filter-select"
              >
                <option value="ALL">All Levels</option>
                <option value="ERROR">Errors</option>
                <option value="WARN">Warnings</option>
                <option value="INFO">Info</option>
                <option value="DEBUG">Debug</option>
              </select>
              <button onClick={exportLogs} className="btn btn-secondary">
                Export Logs
              </button>
            </div>
          </div>

          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Level</th>
                  <th>Category</th>
                  <th>Message</th>
                  <th>User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={log.id} className={getLogLevelClass(log.level)}>
                    <td>{formatTimestamp(log.timestamp)}</td>
                    <td>
                      <span className={`log-level ${log.level.toLowerCase()}`}>
                        {log.level}
                      </span>
                    </td>
                    <td>{log.category}</td>
                    <td>{log.message}</td>
                    <td>{log.user_id ? log.user_id.split('|')[1] : '-'}</td>
                    <td>
                      {log.duration_ms && <span>({log.duration_ms}ms) </span>}
                      {log.status_code && <span>[{log.status_code}] </span>}
                      {log.endpoint && <span>{log.method} {log.endpoint}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'activities' && (
        <div className="tab-content">
          <h3>Recent User Activities</h3>
          <div className="activities-table">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Activity</th>
                  <th>Timestamp</th>
                  <th>Role</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {userActivities.map(activity => (
                  <tr key={activity.id}>
                    <td>{activity.auth0_user_id.split('|')[1]}</td>
                    <td>{activity.activity_type}</td>
                    <td>{formatTimestamp(activity.timestamp)}</td>
                    <td>
                      <span className={`role-badge ${activity.user_role}`}>
                        {activity.user_role}
                      </span>
                    </td>
                    <td>
                      {activity.facility_id && `Facility: ${activity.facility_id}`}
                      {activity.location_lat && ` (${activity.location_lat}, ${activity.location_lon})`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="tab-content">
          <h3>User Management</h3>
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Registered</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Activities</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.email}</strong>
                    </td>
                    <td>{user.name}</td>
                    <td>
                      <span className={`role-badge ${user.role.toLowerCase()}`}>
                        {user.role === 'admin' ? '👑' : '👤'} {user.role.toUpperCase()}
                      </span>
                    </td>
                    <td>{formatTimestamp(user.created_at)}</td>
                    <td>{user.last_login ? formatTimestamp(user.last_login) : 'Never'}</td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? '🟢 Active' : '🔴 Inactive'}
                      </span>
                    </td>
                    <td>
                      {userActivities.filter(activity => activity.auth0_user_id === user.auth0_user_id).length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
            Total Users: {users.length} | Admin Users: {users.filter(u => u.role === 'admin').length} | General Users: {users.filter(u => u.role === 'general').length}
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="tab-content">
          <h3>Active User Sessions</h3>
          <div className="sessions-grid">
            {sessions.map(session => (
              <div key={session.session_id} className="session-card">
                <div className="session-header">
                  <h4>Session: {session.session_id.substring(0, 8)}...</h4>
                  <span className="session-status active">Active</span>
                </div>
                <div className="session-details">
                  <p><strong>User:</strong> {session.user_id.split('|')[1]}</p>
                  <p><strong>Started:</strong> {formatTimestamp(session.start_time)}</p>
                  <p><strong>Last Activity:</strong> {formatTimestamp(session.last_activity)}</p>
                  <p><strong>Actions:</strong> {session.activity_count}</p>
                  <p><strong>Pages:</strong> {session.pages_visited.join(', ')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="tab-content">
          <h3>System Analytics</h3>
          <div className="analytics-grid">
            <div className="metric-card">
              <h4>Total Users</h4>
              <div className="metric-value">{new Set(userActivities.map(a => a.auth0_user_id)).size}</div>
            </div>
            <div className="metric-card">
              <h4>Active Sessions</h4>
              <div className="metric-value">{sessions.length}</div>
            </div>
            <div className="metric-card">
              <h4>Error Rate</h4>
              <div className="metric-value">
                {((logs.filter(l => l.level === 'ERROR').length / logs.length) * 100).toFixed(1)}%
              </div>
            </div>
            <div className="metric-card">
              <h4>Avg Response Time</h4>
              <div className="metric-value">
                {logs.filter(l => l.duration_ms).length > 0 
                  ? Math.round(logs.filter(l => l.duration_ms).reduce((sum, l) => sum + (l.duration_ms || 0), 0) 
                    / logs.filter(l => l.duration_ms).length)
                  : 0}ms
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPanel;